import { classifyHostedReportState } from './hostedReport.js';
import type { HostedReportState, HostedReportSummary } from '../types/hostedReport.js';

// Tools → HTML Hosting — pure list-view logic for the management page.

export type HostedReportFilters = {
  search: string;
  accountId: string | null;
  createdBy: string | null;
  /** null = any state. */
  state: HostedReportState | null;
};

export const EMPTY_HOSTED_REPORT_FILTERS: HostedReportFilters = {
  search: '',
  accountId: null,
  createdBy: null,
  state: null,
};

/**
 * A row's state, derived at RENDER time from its timestamps — the same rule the
 * server applies at read time, through the same shared function. If these two
 * ever disagreed, the list would show a link as live that the viewer refuses
 * (or the reverse), which is the confusing half-state.
 */
export function reportState(report: HostedReportSummary, nowMs: number): HostedReportState {
  return classifyHostedReportState(
    {
      revokedAtMs: report.revokedAt ? Date.parse(report.revokedAt) : null,
      expiresAtMs: report.expiresAt === null ? null : Date.parse(report.expiresAt),
    },
    nowMs,
  );
}

export function filterHostedReports(
  reports: HostedReportSummary[],
  filters: HostedReportFilters,
  nowMs: number,
): HostedReportSummary[] {
  const needle = filters.search.trim().toLowerCase();
  return reports.filter((r) => {
    if (filters.accountId && r.accountId !== filters.accountId) return false;
    if (filters.createdBy && r.createdBy !== filters.createdBy) return false;
    if (filters.state && reportState(r, nowMs) !== filters.state) return false;
    if (needle) {
      const haystack = [r.title, r.accountName ?? '', r.createdByName]
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    return true;
  });
}

/** Distinct {value,label} options for a facet, derived from the rows on screen. */
export function accountOptions(
  reports: HostedReportSummary[],
): Array<{ value: string; label: string }> {
  const seen = new Map<string, string>();
  for (const r of reports) {
    if (r.accountId && !seen.has(r.accountId)) {
      seen.set(r.accountId, r.accountName ?? r.accountId);
    }
  }
  return [...seen.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function creatorOptions(
  reports: HostedReportSummary[],
): Array<{ value: string; label: string }> {
  const seen = new Map<string, string>();
  for (const r of reports) {
    if (!seen.has(r.createdBy)) seen.set(r.createdBy, r.createdByName);
  }
  return [...seen.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '—';
  return new Date(ms).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * The Expires column. ⚠️ A never-expiring report reads "Never", NOT the "—"
 * `formatDateTime` gives a null: an empty-looking cell in an expiry column reads
 * as missing data, when it is the most important fact about that link.
 */
export const NEVER_EXPIRES_LABEL = 'Never';

export function formatExpiry(iso: string | null): string {
  return iso === null ? NEVER_EXPIRES_LABEL : formatDateTime(iso);
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}
